import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { device } from '../../../utils/constants';
import {
  LabBottomRule,
  labAos,
  labBodyMobile,
  labCaptionMobile,
  labSectionPadMobile,
  labSubtitleMobile,
  labTitleMobile,
} from './labShared';

const base = process.env.PUBLIC_URL || '';
const LAB_LOGO_SRC = `${base}/img/lab/PLP_LAB_BLANCO.png`;

const Section = styled.section`
  color: #ffffff;
  font-family: 'MontserratMedium', Helvetica, Arial, sans-serif;
  box-sizing: border-box;
  padding-top: 5vw;
  padding-bottom: 8vw;
  ${labSectionPadMobile}

  @media ${device.mobile} {
    padding-bottom: 70vw;
  }
`;

const Inner = styled.div`
  max-width: min(52rem, 100%);
  margin: 0 auto;
`;

const ContactHeading = styled.h2`
  text-align: left;
  font-family: 'Horizon';
  font-size: 3vw;
  text-transform: uppercase;
  ${labTitleMobile}
`;

const HeadingRuleBlock = styled.div`
  margin: 4vw 0;

  @media ${device.mobile} {
    margin: clamp(1.5rem, 6vw, 2.5rem) 0;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 4vw;
  margin-top: 3vw;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: clamp(2rem, 8vw, 3rem);
    margin-top: clamp(1.25rem, 5vw, 2rem);
  }
`;

const ContactColumn = styled.div`
  text-align: left;
`;

const ColumnHeading = styled.h3`
  margin: 0 0 2vw;
  font-family: 'TelegrafUltraBold';
  font-size: 1.3vw;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  ${labSubtitleMobile}

  @media ${device.mobile} {
    margin-bottom: clamp(0.75rem, 3vw, 1rem);
  }
`;

const PersonBlock = styled.div`
  & + & {
    margin-top: 2.5vw;
  }
`;

const PersonName = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: 1.5vw;
  ${labBodyMobile}
`;

const PersonMeta = styled.p`
  margin: 0.25rem 0 0;
  font-family: 'TelegrafLight';
  font-size: 1.15vw;
  line-height: 1.45;
  opacity: 0.95;
  ${labCaptionMobile}
`;

const EmailLink = styled.a`
  color: inherit;
  text-decoration: none;
  word-break: break-word;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  margin-top: 3vw;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    margin-top: clamp(2rem, 8vw, 3rem);
    gap: clamp(1.5rem, 6vw, 2.5rem);
  }
`;

const FooterContact = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
`;

const LogoRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;

  @media ${device.mobile} {
    width: 100%;
    justify-content: center;
    margin-top: 3em;
  }
`;

const FooterLogo = styled.img`
  width: 14vw;
  height: auto;
  display: block;

  @media ${device.mobile} {
    width: min(50vw, 180px);
  }
`;

const ContactLab = () => {
  const { t } = useTranslation();

  return (
    <Section aria-labelledby="lab-contact-heading">
      <Inner>
        <ContactHeading id="lab-contact-heading" {...labAos('fade-up')}>
          {t('lab.contact.title')}
        </ContactHeading>
      </Inner>

      <HeadingRuleBlock {...labAos('fade-up', 100)}>
        <LabBottomRule aria-hidden />
      </HeadingRuleBlock>

      <Inner>
        <ContactGrid>
          <ContactColumn {...labAos('fade-up', 150)}>
            <ColumnHeading>{t('lab.contact.platformInquiries')}</ColumnHeading>
            <PersonBlock {...labAos('fade-up', 220)}>
              <PersonName>{t('lab.contact.valentinaSperandio')}</PersonName>
              <PersonMeta>
                {t('lab.contact.vpBusinessDevelopmentPartnerships')}
              </PersonMeta>
              <PersonMeta>
                <EmailLink href="mailto:vsperandio@palospanas.com">
                  vsperandio@palospanas.com
                </EmailLink>
              </PersonMeta>
            </PersonBlock>
          </ContactColumn>

          <ContactColumn {...labAos('fade-up', 150)}>
            <ColumnHeading>{t('lab.contact.brandPartnerships')}</ColumnHeading>
            <PersonBlock {...labAos('fade-up', 220)}>
              <PersonName>{t('lab.contact.valentinaSperandio')}</PersonName>
              <PersonMeta>
                {t('lab.contact.vpBusinessDevelopmentPartnerships')}
              </PersonMeta>
              <PersonMeta>
                <EmailLink href="mailto:vsperandio@palospanas.com">
                  vsperandio@palospanas.com
                </EmailLink>
              </PersonMeta>
            </PersonBlock>
          </ContactColumn>
        </ContactGrid>

        <FooterRow {...labAos('fade-up', 300)}>
          <FooterContact {...labAos('fade-up', 380)}>
            <PersonName>{t('lab.contact.nathalieSarShalom')}</PersonName>
            <PersonMeta>{t('lab.contact.leadProducer')}</PersonMeta>
            <PersonMeta>
              <EmailLink href="mailto:nsarsha@palospanas.com">
                nsarsha@palospanas.com
              </EmailLink>
            </PersonMeta>
            <PersonMeta>
              <EmailLink href="tel:+18188252861">+1 (818) 825-2861</EmailLink>
            </PersonMeta>
          </FooterContact>
          <LogoRow {...labAos('zoom-in', 450)}>
            <FooterLogo src={LAB_LOGO_SRC} alt="PLP Lab" />
          </LogoRow>
        </FooterRow>
      </Inner>
    </Section>
  );
};

export default ContactLab;
